import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUpload, FiImage, FiTrash2, FiMaximize2, FiEye } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../api/api';
import { DEFAULT_PRODUCT_IMAGE } from '../../utils/constants';

const ImagePreviewModal = ({ image, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="relative max-w-4xl w-full bg-transparent rounded-lg overflow-hidden">
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
      >
        <FiTrash2 />
      </button>
      <img 
        src={image.images || DEFAULT_PRODUCT_IMAGE} 
        alt="Preview" 
        className="w-full h-auto"
        onError={(e) => {
          e.target.src = DEFAULT_PRODUCT_IMAGE;
        }}
      />
      <div className="p-4 bg-white">
        <p className="text-sm text-gray-600">Image ID: {image.id}</p>
        <p className="text-sm text-gray-600">Upload Date: {new Date(image.created_at).toLocaleDateString()}</p>
      </div>
    </div>
  </div>
);

const VariantImage = ({ image, index, onPreview, onDelete }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className="relative group aspect-square"
    >
      {!imageError ? (
        <img
          src={image.images || DEFAULT_PRODUCT_IMAGE}
          alt={`Variant ${index + 1}`}
          className="w-full h-full object-cover rounded-lg"
          onError={(e) => {
            console.error('Failed to load image:', image.images);
            setImageError(true);
            e.target.src = DEFAULT_PRODUCT_IMAGE;
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
          <FiImage className="w-8 h-8 text-gray-400" />
        </div>
      )}
      
      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
        <button
          onClick={() => onPreview(image)}
          className="p-2 bg-white text-gray-800 rounded-full hover:bg-gray-100 transition-colors"
          title="Preview"
        >
          <FiEye className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(image.id)}
          className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          title="Delete"
        >
          <FiTrash2 className="w-4 h-4" />
        </button>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
        {new Date(image.created_at).toLocaleDateString()}
      </div>
    </motion.div>
  );
};

const UploadForm = ({ productId, variantId, onUploadSuccess }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('images', file);
    });

    try {
      const response = await api.uploadVariantImage(productId, variantId, formData);
      toast.success('Images uploaded successfully');
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onUploadSuccess) onUploadSuccess(response);
    } catch (error) {
      toast.error(error.message || 'Failed to upload images');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center space-x-4">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="block w-full text-sm text-gray-500
            file:mr-4 file:py-2 file:px-4
            file:rounded-md file:border-0
            file:text-sm file:font-semibold
            file:bg-indigo-50 file:text-indigo-700
            hover:file:bg-indigo-100"
        />
        <button
          type="button"
          onClick={handleUpload}
          disabled={isUploading || selectedFiles.length === 0}
          className={`px-4 py-2 rounded-md text-white font-medium ${
            isUploading || selectedFiles.length === 0
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700'
          }`}
        >
          {isUploading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Uploading...
            </span>
          ) : (
            'Upload Images'
          )}
        </button>
      </div>
      {selectedFiles.length > 0 && (
        <div className="text-sm text-gray-600">
          Selected {selectedFiles.length} file(s)
        </div>
      )}
    </div>
  );
};

const VariantImageManager = ({ productId, variant, onImagesChange }) => {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadImages();
  }, [variant.id]);

  const loadImages = async () => {
    if (!variant.id) return;
    
    setIsLoading(true);
    try {
      const fetchedImages = await api.getVariantImages(productId, variant.id);
      setImages(fetchedImages);
    } catch (error) {
      toast.error('Failed to load images');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Validate file sizes and types
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(`File ${file.name} is too large. Maximum size is 5MB`);
        return false;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`File ${file.name} is not an image`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    const formData = new FormData();
    validFiles.forEach(file => {
      formData.append('images', file);
    });

    const loadingToast = toast.loading(`Uploading ${validFiles.length} image(s)...`);
    try {
      const response = await api.uploadVariantImage(productId, variant.id, formData);
      const newImages = Array.isArray(response) ? response : [response];
      
      setImages(prev => [...prev, ...newImages]);
      toast.success(`Successfully uploaded ${newImages.length} image(s)`);
      if (onImagesChange) onImagesChange([...images, ...newImages]);
    } catch (error) {
      toast.error('Failed to upload images');
      console.error(error);
    } finally {
      toast.dismiss(loadingToast);
      event.target.value = '';
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!imageId) {
      toast.error('Image ID is required');
      return;
    }

    // Create a custom toast for delete confirmation
    toast((t) => (
      <div className="flex flex-col gap-4 p-3">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-red-100 rounded-full">
            <FiTrash2 className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Delete Image</h3>
            <p className="text-sm text-gray-500">
              Are you sure you want to delete this image? This action cannot be undone.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              // Show loading toast
              const loadingToast = toast.loading('Deleting image...');
              
              try {
                await api.deleteVariantImage(productId, variant.id, imageId);
                
                // Update local state to remove the deleted image
                setImages(prev => prev.filter(img => img.id !== imageId));
                
                toast.dismiss(loadingToast);
                toast.success('Image deleted successfully');
                
                // Notify parent component if needed
                if (onImagesChange) {
                  onImagesChange(images.filter(img => img.id !== imageId));
                }
              } catch (error) {
                toast.dismiss(loadingToast);
                toast.error(error.message || 'Failed to delete image');
                console.error('Delete image error:', error);
              }
            }}
            className="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    ), {
      duration: Infinity, // Toast won't auto-dismiss
      position: 'top-center',
      style: {
        maxWidth: '400px',
        width: '100%',
        padding: '0',
        backgroundColor: 'white',
      },
    });
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold">
            Images for {variant.color} - {variant.size}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Total images: {images.length}
          </p>
        </div>
      </div>

      {/* Upload Form */}
      <UploadForm 
        productId={productId}
        variantId={variant.id}
        onUploadSuccess={(newImages) => {
          setImages(prev => [...prev, ...(Array.isArray(newImages) ? newImages : [newImages])]);
          if (onImagesChange) {
            onImagesChange([...images, ...(Array.isArray(newImages) ? newImages : [newImages])]);
          }
        }}
      />

      {/* Image Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          <AnimatePresence>
            {images.map((image, index) => (
              <VariantImage
                key={image.id}
                image={image}
                index={index}
                onPreview={setSelectedImage}
                onDelete={handleDeleteImage}
              />
            ))}
          </AnimatePresence>

          {images.length === 0 && !isLoading && (
            <div className="col-span-full flex items-center justify-center h-40 border-2 border-dashed border-gray-300 rounded-lg">
              <div className="text-center">
                <FiImage className="w-8 h-8 mx-auto text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No images uploaded yet</p>
                <p className="text-xs text-gray-400 mt-1">Upload images to get started</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {selectedImage && (
        <ImagePreviewModal 
          image={selectedImage} 
          onClose={() => setSelectedImage(null)} 
        />
      )}

      {/* Image Stats */}
      {images.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Image Information</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Total Images</p>
              <p className="font-medium">{images.length}</p>
            </div>
            <div>
              <p className="text-gray-500">Latest Upload</p>
              <p className="font-medium">
                {images.length > 0 
                  ? new Date(Math.max(...images.map(img => new Date(img.created_at)))).toLocaleDateString()
                  : 'N/A'
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantImageManager;













